// src/pages/calendar/components/CalendarEventForm.js
import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  Switch,
  FormControl,
  FormHelperText,
  Box,
  Chip,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import {
  Title as TitleIcon,
  Description as DescriptionIcon,
  AccessTime as AccessTimeIcon,
  Person as PersonIcon,
  Flag as FlagIcon,
  LocationOn as LocationIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";

import SuiButton from "components/SuiButton";
import SuiInput from "components/SuiInput";
import SuiTypography from "components/SuiTypography";
import SuiBox from "components/SuiBox";

import UserApi from "api/userApi";
import useUserRoles from "hooks/useUserRoles";

const CalendarEventForm = ({ isOpen, onClose, selectedDate, onSubmit, isEditMode = false }) => {
  const { user } = useUserRoles();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);

  // Convertir fechas a formato ISO string para los inputs datetime-local
  const formatDateForInput = (date) => {
    const d = new Date(date);
    // Asegurarse de que la fecha es válida
    if (isNaN(d.getTime())) {
      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, "0");
      const day = String(now.getDate()).padStart(2, "0");
      const hours = String(now.getHours()).padStart(2, "0");
      const minutes = String(now.getMinutes()).padStart(2, "0");
      return `${year}-${month}-${day}T${hours}:${minutes}`;
    }

    // Obtener componentes en hora LOCAL (no UTC) para evitar desfase de zona horaria
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");

    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    start_time: formatDateForInput(selectedDate?.start || new Date()),
    end_time: formatDateForInput(selectedDate?.end || new Date()),
    priority: "medium",
    status: "pending",
    location: "",
    is_all_day: selectedDate?.allDay || false,
    reminders: [],
    assigned_to: "",
  });

  useEffect(() => {
    if (user) {
      fetchUsers();
    }
  }, [user]);

  useEffect(() => {
    // Si estamos en modo edición, cargar los datos del evento
    if (isEditMode && selectedDate && selectedDate.extendedProps) {
      setFormData({
        title: selectedDate.title || "",
        description: selectedDate.extendedProps.description || "",
        start_time: formatDateForInput(selectedDate.start),
        end_time: formatDateForInput(selectedDate.end),
        priority: selectedDate.extendedProps.priority || "medium",
        status: selectedDate.extendedProps.status || "pending",
        location: selectedDate.extendedProps.location || "",
        is_all_day: selectedDate.allDay || false,
        reminders: selectedDate.extendedProps.reminders || [],
        assigned_to: selectedDate.extendedProps.assignedTo.identificacion || "",
        created_by: selectedDate.extendedProps.createdBy.identificacion || "",
      });
    } else if (!isEditMode && selectedDate) {
      // Modo crear nuevo evento
      setFormData({
        title: "",
        description: "",
        start_time: formatDateForInput(selectedDate.start),
        end_time: formatDateForInput(selectedDate.end),
        priority: "medium",
        status: "pending",
        location: "",
        is_all_day: selectedDate.allDay || false,
        reminders: [],
        assigned_to: "",
        created_by: "",
      });
    }
  }, [isEditMode, selectedDate]);

  const fetchUsers = async () => {
    try {
      const response = await UserApi.fetchUsuarios();
      setUsers(response.data);
    } catch (error) {
      console.error("Error fetching users:", error);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const dataToSubmit = {
        ...formData,
      };
      await onSubmit(dataToSubmit);
    } catch (error) {
      console.error("Error submitting form:", error);
    } finally {
      setLoading(false);
    }
  };

  const getCreatedByName = () => {
    const createdBy = selectedDate.extendedProps?.createdBy;
    if (typeof createdBy === "object" && createdBy !== null) {
      return createdBy.nombre_completo || "N/A";
    }
    return createdBy || "N/A";
  };

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="md" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <SuiTypography variant="h5" fontWeight="medium">
              {isEditMode ? "Editar Evento" : "Crear Nuevo Evento"}
            </SuiTypography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1}>
            {/* Título */}
            <Grid size={6}>
              <SuiBox>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <TitleIcon fontSize="small" color="primary" />
                  <SuiTypography variant="button" fontWeight="medium">
                    Título del Evento *
                  </SuiTypography>
                </Box>
                <SuiInput
                  fullWidth
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  required
                  placeholder="Ej: Reunión con cliente"
                />
                <FormHelperText>El título del evento que aparecerá en el calendario</FormHelperText>
              </SuiBox>
            </Grid>

            {/* Prioridad */}
            <Grid size={{ xs: 6, md: 6 }}>
              <SuiBox>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <FlagIcon fontSize="small" color="primary" />
                  <SuiTypography variant="button" fontWeight="medium">
                    Prioridad *
                  </SuiTypography>
                </Box>
                <FormControl fullWidth>
                  <Select name="priority" value={formData.priority} onChange={handleInputChange}>
                    <MenuItem value="low">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label="Baja" color="success" size="small" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="medium">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label="Media" color="warning" size="small" />
                      </Box>
                    </MenuItem>
                    <MenuItem value="high">
                      <Box display="flex" alignItems="center" gap={1}>
                        <Chip label="Alta" color="error" size="small" />
                      </Box>
                    </MenuItem>
                  </Select>
                </FormControl>
              </SuiBox>
            </Grid>

            {/* Descripción */}
            <Grid size={12}>
              <SuiBox>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <DescriptionIcon fontSize="small" color="primary" />
                  <SuiTypography variant="button" fontWeight="medium">
                    Descripción
                  </SuiTypography>
                </Box>
                <SuiInput
                  fullWidth
                  multiline
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Detalles adicionales sobre el evento..."
                />
                <FormHelperText>
                  Información adicional que ayude a contextualizar el evento
                </FormHelperText>
              </SuiBox>
            </Grid>

            {/* Fechas y Horas */}
            <Grid size={12}>
              <Box display="flex" alignItems="center" gap={1}>
                <AccessTimeIcon fontSize="small" color="primary" />
                <SuiTypography variant="button" fontWeight="medium">
                  Fecha y Hora
                </SuiTypography>
              </Box>
            </Grid>
            <Grid size={{ xs: 4, md: 4 }}>
              <SuiBox>
                <SuiTypography variant="caption" fontWeight="regular" mb={1} display="block">
                  Fecha y hora de inicio *
                </SuiTypography>
                <SuiInput
                  fullWidth
                  type="datetime-local"
                  name="start_time"
                  value={formData.start_time}
                  onChange={handleInputChange}
                  required
                />
              </SuiBox>
            </Grid>

            <Grid size={{ xs: 4, md: 4 }}>
              <SuiBox>
                <SuiTypography variant="caption" fontWeight="regular" mb={1} display="block">
                  Fecha y hora de fin *
                </SuiTypography>
                <SuiInput
                  fullWidth
                  type="datetime-local"
                  name="end_time"
                  value={formData.end_time}
                  onChange={handleInputChange}
                  required
                />
              </SuiBox>
            </Grid>

            {/* Todo el día */}
            <Grid size={{ xs: 4, md: 4 }}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{
                  p: 2,
                  backgroundColor: "rgba(0, 0, 0, 0.02)",
                  borderRadius: 2,
                  border: "1px solid rgba(0, 0, 0, 0.08)",
                }}
              >
                <SuiTypography variant="button" fontWeight="medium">
                  Todo el día
                </SuiTypography>
                <Switch
                  checked={formData.is_all_day}
                  onChange={(e) =>
                    handleInputChange({
                      target: {
                        name: "is_all_day",
                        value: e.target.checked,
                      },
                    })
                  }
                  name="is_all_day"
                />
              </Box>
            </Grid>

            {/* Usuario Asignado */}
            <Grid size={{ xs: 6, md: 6 }}>
              <SuiBox>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <PersonIcon fontSize="small" color="primary" />
                  <SuiTypography variant="button" fontWeight="medium">
                    Usuario Responsable *
                  </SuiTypography>
                </Box>
                <FormControl fullWidth required>
                  <Select
                    name="assigned_to"
                    value={formData.assigned_to}
                    onChange={handleInputChange}
                    displayEmpty
                  >
                    <MenuItem value="" disabled>
                      Seleccionar Responsable
                    </MenuItem>
                    {users.map((userItem) => (
                      <MenuItem key={userItem.id} value={userItem.identificacion}>
                        {userItem.nombre_completo}
                      </MenuItem>
                    ))}
                  </Select>
                  <FormHelperText>Persona responsable de este evento</FormHelperText>
                </FormControl>
              </SuiBox>
            </Grid>

            {/* Ubicación */}
            <Grid size={{ xs: 6, md: 6 }}>
              <SuiBox>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <LocationIcon fontSize="small" color="primary" />
                  <SuiTypography variant="button" fontWeight="medium">
                    Ubicación
                  </SuiTypography>
                </Box>
                <SuiInput
                  fullWidth
                  name="location"
                  value={formData.location}
                  onChange={handleInputChange}
                  placeholder="Ej: Sala de juntas"
                />
                <FormHelperText>Lugar donde se realizará el evento</FormHelperText>
              </SuiBox>
            </Grid>

            {/* Recordatorios */}
            <Grid size={{ xs: 6, md: 6 }}>
              <SuiBox>
                <Box display="flex" alignItems="center" gap={1} mb={1}>
                  <NotificationsIcon fontSize="small" color="primary" />
                  <SuiTypography variant="button" fontWeight="medium">
                    Recordatorios
                  </SuiTypography>
                </Box>
                <FormControl fullWidth>
                  <Select
                    multiple
                    name="reminders"
                    value={formData.reminders}
                    onChange={handleInputChange}
                    displayEmpty
                    renderValue={(selected) => {
                      if (selected.length === 0) {
                        return <em>Sin recordatorios</em>;
                      }
                      return (
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {selected.map((value) => (
                            <Chip key={value} label={`${value} min`} size="small" color="info" />
                          ))}
                        </Box>
                      );
                    }}
                  >
                    <MenuItem value={5}>5 minutos antes</MenuItem>
                    <MenuItem value={15}>15 minutos antes</MenuItem>
                    <MenuItem value={30}>30 minutos antes</MenuItem>
                    <MenuItem value={60}>1 hora antes</MenuItem>
                    <MenuItem value={1440}>1 día antes</MenuItem>
                  </Select>
                  <FormHelperText>Selecciona cuándo quieres recibir notificaciones</FormHelperText>
                </FormControl>
              </SuiBox>
            </Grid>

            {/* Estado (solo en modo edición) */}
            {isEditMode && (
              <Grid size={{ xs: 6, md: 6 }}>
                <SuiBox>
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <FlagIcon fontSize="small" color="primary" />
                    <SuiTypography variant="button" fontWeight="medium">
                      Estado *
                    </SuiTypography>
                  </Box>
                  <FormControl fullWidth>
                    <Select name="status" value={formData.status} onChange={handleInputChange}>
                      <MenuItem value="pending">Pendiente</MenuItem>
                      <MenuItem value="in_progress">En progreso</MenuItem>
                      <MenuItem value="completed">Completado</MenuItem>
                      <MenuItem value="cancelled">Cancelado</MenuItem>
                    </Select>
                  </FormControl>
                </SuiBox>
              </Grid>
            )}

            {isEditMode && (
              <Grid size={12}>
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{
                    pt: 2,
                    borderTop: "1px solid rgba(0, 0, 0, 0.08)",
                  }}
                >
                  <SuiTypography variant="caption" textColor="secondary">
                    Creado por: {getCreatedByName()}
                  </SuiTypography>
                  {selectedDate.extendedProps?.created_at && (
                    <SuiTypography variant="caption" textColor="secondary">
                      {new Date(selectedDate.extendedProps.created_at).toLocaleDateString("es-CO")}
                    </SuiTypography>
                  )}
                </Box>
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, mt: -2 }}>
          <SuiButton onClick={onClose} buttonColor="light">
            Cancelar
          </SuiButton>
          <SuiButton type="submit" variant="gradient" buttonColor="dark" disabled={loading}>
            {loading ? "Guardando..." : isEditMode ? "Actualizar Evento" : "Crear Evento"}
          </SuiButton>
        </DialogActions>
      </form>
    </Dialog>
  );
};

CalendarEventForm.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  selectedDate: PropTypes.shape({
    start: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    end: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]),
    allDay: PropTypes.bool,
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    title: PropTypes.string,
    extendedProps: PropTypes.shape({
      description: PropTypes.string,
      priority: PropTypes.string,
      status: PropTypes.string,
      location: PropTypes.string,
      assignedTo: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          identificacion: PropTypes.string,
          nombre_completo: PropTypes.string,
        }),
      ]),
      createdBy: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.shape({
          id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
          identificacion: PropTypes.string,
          nombre_completo: PropTypes.string,
        }),
      ]),
      reminders: PropTypes.arrayOf(PropTypes.number),
      created_at: PropTypes.string,
    }),
  }),
  onSubmit: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool,
};

export default CalendarEventForm;
